import React from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { sensorUnits } from 'commons/src/constants';
import { Rating, SensorCardMode, SensorTypes } from 'commons/src/models/commonEnums';
import { DayUsageHours } from 'commons/src/models/commonTypeScript';
import {
    calculatePeakRanges,
    calculateSpacesByRating,
    DeviceMeasurement,
    getSpacesWithIssues,
    sensorRating,
    SpacesByRating,
} from 'commons/src/sensorRatingUtils';
import { IaqInsightData, ReportSensorThresholds } from '../../../../models/common';
import IaqInsightGraph from '../../iaqInsight/InsightGraph';
import styles from '../SensorsView.module.scss';
import { SensorCard } from './SensorCard';
import { SensorHeader } from './SensorHeader';

export type SingleSensorViewProps = {
    sensor: SensorTypes;
    sensorData: IaqInsightData;
    sensorThreshold: ReportSensorThresholds;
    from: Moment;
    to: Moment;
    usageHours: { [day: string]: DayUsageHours };
    timeZone: string;
};

type SummaryCardConfig = {
    title: string;
    value: number;
    rating: Rating;
    unit: string;
};

type DetailCardConfig = {
    rating: Rating;
    label: string;
    count: number;
    peakRange: { min: number; max: number } | null;
    unit: string;
};

export const SingleSensorView: React.FC<SingleSensorViewProps> = ({
    sensor,
    sensorData,
    sensorThreshold,
    from,
    to,
    usageHours,
    timeZone,
}) => {
    const { t: txt } = useTranslation();

    const averageWithinHours = sensorData.averageValueWithinOpeningHours;
    const { averageValue } = sensorData;
    const averageRating = sensorRating(sensorThreshold.ranges, averageWithinHours);
    const overallRating = sensorRating(sensorThreshold.ranges, averageValue);

    const deviceValues: DeviceMeasurement[] = Object.values(sensorData.devicesOpeningHoursValues || {});
    const spacesByRating: SpacesByRating = calculateSpacesByRating(deviceValues, sensorThreshold.ranges);
    const spacesWithIssues = getSpacesWithIssues(spacesByRating);
    const peakRanges = calculatePeakRanges(spacesByRating);

    const unit = sensorUnits[sensorThreshold.unit];

    const summaryCardConfigs: SummaryCardConfig[] = [
        {
            title: txt('InsightIaq.AverageWithinOpeningHours'),
            value: averageWithinHours || 0,
            rating: averageRating || Rating.GOOD,
            unit,
        },
        {
            title: txt('InsightIaq.AverageValueNoException'),
            value: averageValue || 0,
            rating: overallRating || Rating.GOOD,
            unit,
        },
    ];

    const detailCardConfigs: DetailCardConfig[] = [
        {
            rating: Rating.POOR,
            label: txt('InsightIaq.WithPoorLevels'),
            count: spacesWithIssues.poor,
            peakRange: peakRanges.poor,
            unit,
        },
        {
            rating: Rating.FAIR,
            label: txt('InsightIaq.WithFairLevels'),
            count: spacesWithIssues.fair,
            peakRange: peakRanges.fair,
            unit,
        },
        {
            rating: Rating.GOOD,
            label: txt('InsightIaq.WithNoIssues'),
            count: spacesWithIssues.good,
            peakRange: peakRanges.good,
            unit,
        },
    ];

    return (
        <div className={styles.sensorsView}>
            <SensorHeader sensor={sensor} thresholds={sensorThreshold} />
            <div className={styles.description}>{txt(`InsightIaq.${sensor}Description`)}</div>

            <div className={styles.summaryCards}>
                {summaryCardConfigs.map(config => (
                    <SensorCard
                        key={config.title}
                        mode={SensorCardMode.Summary}
                        title={config.title}
                        value={config.value}
                        unit={config.unit}
                        rating={config.rating}
                    />
                ))}
            </div>

            <div className={styles.detailCards}>
                {detailCardConfigs
                    .filter(
                        (config): config is DetailCardConfig & { peakRange: { min: number; max: number } } =>
                            config.count > 0 && config.peakRange !== null
                    )
                    .map(config => (
                        <SensorCard
                            key={config.rating}
                            mode={SensorCardMode.Detail}
                            count={config.count}
                            label={config.label}
                            rating={config.rating}
                            peakRange={config.peakRange}
                            unit={config.unit}
                        />
                    ))}
            </div>
            <div id={`insightGraph-${sensor}`} className="insight-tile__content insight-tile__content--graph">
                <IaqInsightGraph
                    usageHours={usageHours}
                    chartData={sensorData.averageGraph || []}
                    ranges={sensorThreshold.ranges}
                    unit={sensorThreshold.unit}
                    sensor={sensor}
                    from={from}
                    to={to}
                    graphId={`${sensor}-graph`}
                    timeZone={timeZone}
                />
            </div>
        </div>
    );
};
